import { useQuery } from '@tanstack/react-query'
import { api } from './client'
import type { AddressPayload, CustomerAddress, CustomerSession, Order, OtpVerifyResponse } from '../types'

/** Ask for a login code to be texted to this phone (§9, D-083). Public — no session needed. */
export async function requestOtp(phone: string): Promise<void> {
  await api.post('/otp/request', { phone })
}

/**
 * Verify the code. On success the server resolves the realm and (for a customer) sets the
 * rotating refresh cookie; `name` seeds a brand-new customer account and is ignored otherwise.
 */
export async function verifyOtp(phone: string, code: string, name?: string): Promise<OtpVerifyResponse> {
  const { data } = await api.post<OtpVerifyResponse>('/otp/verify', { phone, code, name })
  return data
}

/** Update the logged-in customer's display name; returns the refreshed session to adopt. */
export async function updateProfileName(name: string): Promise<CustomerSession> {
  const { data } = await api.put<CustomerSession>('/storefront/account/profile', { name })
  return data
}

async function fetchAccountOrders(): Promise<Order[]> {
  const { data } = await api.get<Order[]>('/storefront/account/orders')
  return data
}

/** Order history for the logged-in customer. Only run when authenticated. */
export function useAccountOrders(enabled: boolean) {
  return useQuery({
    queryKey: ['account', 'orders'],
    queryFn: fetchAccountOrders,
    enabled,
    // Poll every 5s while any order is still in flight; stop once they're all terminal.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((o) => o.status !== 'DELIVERED' && o.status !== 'CANCELLED')
        ? 5000
        : false,
  })
}

async function fetchAddresses(): Promise<CustomerAddress[]> {
  const { data } = await api.get<CustomerAddress[]>('/storefront/account/addresses')
  return data
}

export function useAddresses(enabled: boolean) {
  return useQuery({ queryKey: ['account', 'addresses'], queryFn: fetchAddresses, enabled })
}

export async function createAddress(payload: AddressPayload): Promise<CustomerAddress> {
  const { data } = await api.post<CustomerAddress>('/storefront/account/addresses', payload)
  return data
}

export async function updateAddress(id: string, payload: AddressPayload): Promise<CustomerAddress> {
  const { data } = await api.put<CustomerAddress>(`/storefront/account/addresses/${id}`, payload)
  return data
}

export async function deleteAddress(id: string): Promise<void> {
  await api.delete(`/storefront/account/addresses/${id}`)
}
